import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Map as MapLibreMap } from 'maplibre-gl';
import type {
  Viewport,
  MapStyle,
  Coordinates,
  UserLocationState,
  LocationPermissionState,
} from '@/types';
import config from '@/config';

interface MapState {
  // Map instance
  map: MapLibreMap | null;
  isLoaded: boolean;

  // Viewport state
  viewport: Viewport;

  // Style and layers
  currentStyle: MapStyle;
  show3DBuildings: boolean;
  showTransit: boolean;
  showBikeRoutes: boolean;
  showTerrain: boolean;

  // User location
  userLocation: Coordinates | null;
  userLocationState: UserLocationState | null;
  locationPermission: LocationPermissionState;
  isTrackingLocation: boolean;
  isFollowingUser: boolean;
  locationError: string | null;
  showLocationPanel: boolean;

  // Selection
  selectedFeatureId: string | null;

  // Street View
  streetViewCoordinates: Coordinates | null;

  // Actions
  setMap: (map: MapLibreMap | null) => void;
  setIsLoaded: (loaded: boolean) => void;
  setViewport: (viewport: Partial<Viewport>) => void;
  setCurrentStyle: (style: MapStyle) => void;
  toggleLayer: (layer: 'buildings' | 'transit' | 'bike' | 'terrain') => void;
  setUserLocation: (location: Coordinates | null) => void;
  setUserLocationState: (state: UserLocationState | null) => void;
  setLocationPermission: (permission: LocationPermissionState) => void;
  setIsTrackingLocation: (tracking: boolean) => void;
  setIsFollowingUser: (following: boolean) => void;
  setLocationError: (error: string | null) => void;
  setShowLocationPanel: (show: boolean) => void;
  goToUserLocation: () => void;
  setSelectedFeatureId: (id: string | null) => void;
  setStreetViewCoordinates: (coords: Coordinates | null) => void;
  flyTo: (center: Coordinates, zoom?: number) => void;
  fitBounds: (sw: Coordinates, ne: Coordinates, padding?: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
}

export const useMapStore = create<MapState>()(
  persist(
    (set, get) => ({
      // Initial state
      map: null,
      isLoaded: false,
      viewport: {
        center: { lng: config.map.defaultCenter.lng, lat: config.map.defaultCenter.lat },
        zoom: config.map.defaultZoom,
        bearing: 0,
        pitch: 0,
      },
      currentStyle: 'default',
      show3DBuildings: true,
      showTransit: false,
      showBikeRoutes: false,
      showTerrain: false,
      userLocation: null,
      userLocationState: null,
      locationPermission: 'prompt',
      isTrackingLocation: false,
      isFollowingUser: false,
      locationError: null,
      showLocationPanel: false,
      selectedFeatureId: null,
      streetViewCoordinates: null,

      // Actions
      setMap: (map) => set({ map }),

      setIsLoaded: (loaded) => set({ isLoaded: loaded }),

      setViewport: (viewport) => set((state) => ({
        viewport: { ...state.viewport, ...viewport },
      })),

      setCurrentStyle: (style) => {
        set({ currentStyle: style });
        const { map } = get();
        if (map) {
          const styleUrl = getStyleUrl(style);
          map.setStyle(styleUrl);
        }
      },

      // Only flip the boolean state here. MapContainer has effects that react
      // to these flags and add/remove the relevant layer/source with proper
      // guards (e.g. only when the style/source exists) — manipulating the map
      // directly here as well caused crashes when the 3d-buildings layer or
      // terrain source was absent (e.g. after a base-style switch).
      toggleLayer: (layer) => {
        switch (layer) {
          case 'buildings':
            set((state) => ({ show3DBuildings: !state.show3DBuildings }));
            break;
          case 'transit':
            set((state) => ({ showTransit: !state.showTransit }));
            break;
          case 'bike':
            set((state) => ({ showBikeRoutes: !state.showBikeRoutes }));
            break;
          case 'terrain':
            set((state) => ({ showTerrain: !state.showTerrain }));
            break;
        }
      },

      setUserLocation: (location) => set({ userLocation: location }),

      setUserLocationState: (state) => set({
        userLocationState: state,
        userLocation: state ? state.coordinates : null,
      }),

      setLocationPermission: (permission) => set({ locationPermission: permission }),

      setIsTrackingLocation: (tracking) => set({ isTrackingLocation: tracking }),

      setIsFollowingUser: (following) => set({ isFollowingUser: following }),

      setLocationError: (error) => set({ locationError: error }),

      setShowLocationPanel: (show) => set({ showLocationPanel: show }),

      goToUserLocation: () => {
        const { map, userLocation } = get();
        if (map && userLocation) {
          map.flyTo({
            center: [userLocation.lng, userLocation.lat],
            zoom: 16,
            duration: 1500,
          });
        }
      },

      setSelectedFeatureId: (id) => set({ selectedFeatureId: id }),

      setStreetViewCoordinates: (coords) => set({ streetViewCoordinates: coords }),

      flyTo: (center, zoom) => {
        const { map } = get();
        if (map) {
          map.flyTo({
            center: [center.lng, center.lat],
            zoom: zoom ?? map.getZoom(),
            duration: 1500,
          });
        }
        set({ viewport: { ...get().viewport, center, zoom: zoom ?? get().viewport.zoom } });
      },

      fitBounds: (sw, ne, padding = 50) => {
        const { map } = get();
        if (map) {
          map.fitBounds(
            [[sw.lng, sw.lat], [ne.lng, ne.lat]],
            { padding, duration: 1000 }
          );
        }
      },

      zoomIn: () => {
        const { map, viewport } = get();
        const newZoom = Math.min(viewport.zoom + 1, 20);
        if (map) {
          map.easeTo({ zoom: newZoom, duration: 300 });
        }
        set({ viewport: { ...viewport, zoom: newZoom } });
      },

      zoomOut: () => {
        const { map, viewport } = get();
        const newZoom = Math.max(viewport.zoom - 1, 1);
        if (map) {
          map.easeTo({ zoom: newZoom, duration: 300 });
        }
        set({ viewport: { ...viewport, zoom: newZoom } });
      },
    }),
    {
      name: 'tensormaps-map-store',
      partialize: (state) => ({
        viewport: state.viewport,
        currentStyle: state.currentStyle,
        show3DBuildings: state.show3DBuildings,
        showTransit: state.showTransit,
        showBikeRoutes: state.showBikeRoutes,
        showTerrain: state.showTerrain,
      }),
    }
  )
);

// Bundled offline style shipped in public/. Raster-only: no vector POI clicks or
// 3D buildings, but guarantees the map renders even with no tile server reachable.
export const BUNDLED_FALLBACK_STYLE = '/map-styles/osm-bright.json';

// Resolve a base-style URL from the single source of truth (config.map.styles),
// so switching styles at runtime uses the exact same URLs as the initial load.
// Only 'default', 'dark', and (when configured) 'satellite' are real styles; any
// legacy/unsupported value falls back to the default street style rather than a
// stubbed one, and the bundled raster style is the final offline fallback.
export function getStyleUrl(style: MapStyle): string {
  const styles = config.map.styles;
  const fallback = styles.default || BUNDLED_FALLBACK_STYLE;

  switch (style) {
    case 'dark':
      return styles.dark || fallback;
    case 'satellite':
      return styles.satellite || fallback;
    default:
      return fallback;
  }
}
